// src/Contact.jsx
import React from 'react';
import NavigationBar from './NavigationBar';
import './PolicyPages.css';

const Contact = () => {
  return (
    <>
      <NavigationBar />
      <div className="policy-page-container">
        <div className="policy-content">
          <h1>Contact Us</h1>
       
          <section>
            <h2>Get in Touch</h2>
            <p>
              We're here to help with questions about our <strong>data aggregation and analysis platform</strong>. 
              Whether you need technical support, have billing questions, or want to provide feedback, 
              we're committed to responding promptly.
            </p>
            <p>
              <strong>Please note:</strong> TickrTrends is a software tool that provides market data and analysis 
              for educational purposes. We do NOT provide personalized investment advice or recommendations.
            </p>
          </section>

          <section>
            <h2>Support Email</h2>
            <p className="contact-info">
              📧 <a href="mailto:ryan@tickrtrends.com">ryan@tickrtrends.com</a>
            </p>
            <p>
              <strong>Response Time:</strong> We typically respond within 1-2 business days 
              (excluding weekends and U.S. federal holidays).
            </p>
            <p>
              <strong>For Faster Service:</strong> Please include a clear subject line and your account 
              email address in your message.
            </p>
          </section>

          <section>
            <h2>What We Can Help With</h2>
            
            <h3>Technical Support</h3>
            <ul>
              <li>Login or authentication issues</li>
              <li>Website errors or bugs</li>
              <li>Data loading problems</li>
              <li>Feature questions or functionality</li>
              <li>Browser compatibility issues</li>
              <li>Mobile access problems</li>
            </ul>

            <h3>Account & Subscription</h3>
            <ul>
              <li>Account creation or access issues</li>
              <li>Subscription management and cancellation</li>
              <li>Password reset assistance</li>
              <li>Email address changes</li>
              <li>Account deletion requests</li>
            </ul>

            <h3>Billing & Payments</h3>
            <ul>
              <li>Payment method updates</li>
              <li>Billing questions or disputes</li>
              <li>Refund requests (see our Refund Policy)</li>
              <li>Invoice or receipt requests</li>
              <li>Failed payment assistance</li>
            </ul>

            <h3>General Inquiries</h3>
            <ul>
              <li>Product features and roadmap</li>
              <li>Data sources and methodology questions</li>
              <li>Educational content about using the platform</li>
              <li>Feedback and feature requests</li>
            </ul>
          </section>

          <section>
            <h2>What We Cannot Help With</h2>
            <div style={{ 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '20px'
            }}>
              <h3 style={{ color: '#ef4444', marginTop: 0 }}>⚠️ Important Limitations</h3>
              <p>
                <strong>We CANNOT provide:</strong>
              </p>
              <ul>
                <li>Personalized investment advice or recommendations</li>
                <li>Opinions on whether to buy, sell, or hold specific securities</li>
                <li>Portfolio management or allocation guidance</li>
                <li>Tax, legal, or financial planning advice</li>
                <li>Predictions about future market performance</li>
                <li>Guarantees about investment returns</li>
              </ul>
              <p>
                <strong>Why?</strong> TickrTrends is a data and analysis tool, not a registered investment advisor. 
                For personalized investment guidance, please consult a licensed financial professional.
              </p>
            </div>
          </section>

          <section>
            <h2>Business Inquiries</h2>
            <p>
              For partnership opportunities, media inquiries, institutional licensing, 
              or other business-related questions, please contact:
              <br />
              <strong>Email:</strong> ryan@tickrtrends.com
              <br />
              <strong>Subject Line:</strong> "Business Inquiry - [Your Topic]"
            </p>
          </section>

          <section>
            <h2>Privacy & Data Requests</h2>
            <p>
              To exercise your privacy rights under GDPR, CCPA, or other regulations:
            </p>
            <ul>
              <li><strong>Data Access Request:</strong> Request a copy of your personal data</li>
              <li><strong>Data Deletion Request:</strong> Request deletion of your account and data</li>
              <li><strong>Data Correction:</strong> Update inaccurate information</li>
              <li><strong>Data Portability:</strong> Receive your data in portable format</li>
            </ul>
            <p>
              Email: ryan@tickrtrends.com with subject "Privacy Request"
              <br />
              <strong>Response Time:</strong> Within 30 days as required by law
            </p>
            <p>
              See our <a href="/privacy-policy" style={{ color: '#8b5cf6' }}>Privacy Policy</a> for more details.
            </p>
          </section>

          <section>
            <h2>Before You Contact Us</h2>
            <p>
              For faster assistance, please check if your question is answered in our documentation:
            </p>
            <ul>
              <li><a href="/terms-of-service" style={{ color: '#8b5cf6' }}>Terms of Service</a> - Platform rules and limitations</li>
              <li><a href="/privacy-policy" style={{ color: '#8b5cf6' }}>Privacy Policy</a> - How we handle your data</li>
              <li><a href="/refund-policy" style={{ color: '#8b5cf6' }}>Refund Policy</a> - Cancellation and refund information</li>
            </ul>
          </section>

          <section>
            <h2>Response Expectations</h2>
            
            <h3>Standard Inquiries</h3>
            <p>
              Most questions receive a response within <strong>1-2 business days</strong> 
              (Monday-Friday, 9 AM - 5 PM Central Time, excluding U.S. federal holidays).
            </p>

            <h3>Complex Issues</h3>
            <p>
              Technical investigations, refund reviews, or billing disputes may require 
              <strong>3-5 business days</strong> for thorough investigation and resolution.
            </p>

            <h3>Privacy Requests</h3>
            <p>
              Data access, deletion, or portability requests will be processed within 
              <strong>30 days</strong> as required by applicable privacy laws.
            </p>

            <h3>During High Volume Periods</h3>
            <p>
              Response times may be longer during:
            </p>
            <ul>
              <li>Product launches or major updates</li>
              <li>Market volatility events (high user activity)</li>
              <li>Holiday periods</li>
            </ul>
            <p>
              We appreciate your patience and will respond as quickly as possible.
            </p>
          </section>

          <section>
            <h2>Email Best Practices</h2>
            <p>
              To help us assist you more efficiently, please:
            </p>
            <ul>
              <li><strong>Use a clear subject line</strong> (e.g., "Billing Question - Duplicate Charge")</li>
              <li><strong>Include your account email</strong> so we can locate your account</li>
              <li><strong>Describe the issue clearly</strong> with specific details or screenshots</li>
              <li><strong>Send one email per issue</strong> rather than multiple emails</li>
              <li><strong>Avoid sending duplicate emails</strong> - this delays responses</li>
            </ul>
          </section>

          <section style={{ 
            background: 'rgba(59, 130, 246, 0.1)', 
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '30px'
          }}>
            <h2 style={{ color: '#3b82f6', marginTop: 0 }}>📧 Ready to Contact Us?</h2>
            <p style={{ marginBottom: '10px' }}>
              <strong>Email:</strong> <a href="mailto:ryan@tickrtrends.com" style={{ color: '#3b82f6' }}>ryan@tickrtrends.com</a>
            </p>
            <p style={{ marginBottom: '10px' }}>
              <strong>Average Response:</strong> 1-2 business days
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>Hours:</strong> Monday-Friday, 9 AM - 5 PM CT (Email support only)
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Contact;